// src/components/QuickOptionsCard.js
//
// In-chat "quick options" card. KAI emits a fenced ```kai-options JSON block
// when it needs a preference from the user (e.g. pptx template + speaker
// notes); the ChatInterface code-renderer parses that block and renders this
// card instead of raw JSON. The user clicks chips / fills tiny blanks and
// presses one Send, which composes a plain-text answer and sends it as a
// normal user turn via `onSubmit`. Keeps the whole exchange inside the chat
// with no modal or form.
//
// config shape:
//   {
//     "intro": "optional string shown above the fields",
//     "fields": [
//       { "key": "template", "label": "Template style", "type": "choice",
//         "options": ["Sans (modern)", "Serif (classic)"], "default": 0 },
//       { "key": "date", "label": "Meeting date", "type": "text",
//         "placeholder": "e.g. 2026-07-22", "default": "" }
//     ],
//     "submit": "Build deck"
//   }
//
// type "choice"      -> single-select chips (default = option index; null = none
//                       picked, and Send waits until one is)
// type "multichoice" -> chips that toggle; at least one must be picked, and
//                       "max" (optional) caps how many. The reply joins them
//                       with MULTI_CHOICE_SEPARATOR: "Tables: Sales / Budget".
// type "text"        -> inline blank (default = string, optional placeholder)
// A field may carry its own "intro", shown above it (see mergeKaiOptionsCards).

import React, { useState } from 'react';
import './QuickOptionsCard.css';

// Must match _MULTI_CHOICE_SEPARATOR in backend/custom_tools/sharepoint_connect.py,
// which splits the reply back into table names.
const MULTI_CHOICE_SEPARATOR = ' / ';

const isMultiChoice = (field) => field.type === 'multichoice';

const KAI_OPTIONS_BLOCK = /```kai-options[ \t]*\r?\n([\s\S]*?)```/g;

// KAI sometimes asks several things as several ```kai-options blocks in one
// reply. Drawn as separate cards, a one-question card sends the moment its chip
// is clicked, before the user has answered the cards below it. Fold every
// complete block in the message into the first, so it's one card with one Send.
// Each later card's intro stays above its own questions, and a question that
// was a card of its own still starts with nothing picked. A repeated label
// gets a number ("Table 2"): the reply is "<label>: <value>" lines, so two
// "Table" lines couldn't be told apart. Any block that doesn't parse leaves
// the message as it was.
export function mergeKaiOptionsCards(content) {
  if (typeof content !== 'string' || !content.includes('```kai-options')) return content;
  const blocks = [...content.matchAll(KAI_OPTIONS_BLOCK)];
  if (blocks.length < 2) return content;

  let configs;
  try {
    configs = blocks.map((block) => JSON.parse(block[1]));
  } catch (e) {
    return content;
  }
  const isUsable = (config) => config && Array.isArray(config.fields) && config.fields.length
    && config.fields.every((field) => field && typeof field === 'object');
  if (!configs.every(isUsable)) return content;

  const usedKeys = new Set();
  const usedLabels = new Set();
  const fields = [];
  configs.forEach((config, cardIndex) => {
    const wasSingleChoice = config.fields.length === 1 && config.fields[0].type !== 'text';
    config.fields.forEach((field, fieldIndex) => {
      const base = String(field.key || 'field');
      let key = base;
      for (let n = 2; usedKeys.has(key); n += 1) key = `${base}_${n}`;
      usedKeys.add(key);
      const merged = { ...field, key };
      if (typeof field.label === 'string') {
        let label = field.label;
        for (let n = 2; usedLabels.has(label); n += 1) label = `${field.label} ${n}`;
        usedLabels.add(label);
        merged.label = label;
      }
      if (wasSingleChoice) merged.default = null;
      if (cardIndex > 0 && fieldIndex === 0 && config.intro) merged.intro = config.intro;
      fields.push(merged);
    });
  });
  const submit = configs.map((config) => config.submit).filter(Boolean).pop();
  const mergedConfig = {
    ...(configs[0].intro ? { intro: configs[0].intro } : {}),
    fields,
    ...(submit ? { submit } : {}),
  };

  // The merged card takes the first block's place; text between the blocks
  // follows it. Its lines keep the first fence's indent (a card inside a list
  // item): without it the list item ends at the JSON line, and the bare
  // closing fence opens a code block that swallows the rest of the message.
  // A fence right after a list marker ("- ```kai-options", "1. ```...")
  // indents the lines below it to the marker's width.
  const lineStart = content.lastIndexOf('\n', blocks[0].index - 1) + 1;
  const linePrefix = content.slice(lineStart, blocks[0].index);
  const indent = /^[ \t]*(?:(?:[-+*]|\d{1,9}[.)])[ \t]+)*$/.test(linePrefix)
    ? linePrefix.replace(/\S/g, ' ')
    : '';
  let out = `${content.slice(0, blocks[0].index)}\`\`\`kai-options\n${indent}${JSON.stringify(mergedConfig)}\n${indent}\`\`\``;
  blocks.forEach((block, i) => {
    const next = blocks[i + 1];
    out += content.slice(block.index + block[0].length, next ? next.index : content.length);
  });
  return out;
}

function initialValues(fields) {
  const values = {};
  fields.forEach((field) => {
    const options = Array.isArray(field.options) ? field.options : [];
    if (field.type === 'text') {
      values[field.key] = typeof field.default === 'string' ? field.default : '';
    } else if (isMultiChoice(field)) {
      values[field.key] = Array.isArray(field.default)
        ? field.default.filter((option) => options.includes(option))
        : [];
    } else if (field.default === null) {
      values[field.key] = '';
    } else {
      let idx = 0;
      if (typeof field.default === 'number' && field.default >= 0 && field.default < options.length) {
        idx = field.default;
      }
      values[field.key] = options[idx] != null ? options[idx] : '';
    }
  });
  return values;
}

// A choice field still waiting for its answer: one with options to pick from
// and nothing picked. Options can be falsy themselves (0, false), so only an
// empty value counts. A field with no options can't be answered and doesn't
// hold Send back.
function isUnansweredChoice(field, value) {
  return field.type !== 'text' && Array.isArray(field.options) && field.options.length > 0
    && (value === '' || value == null || (Array.isArray(value) && value.length === 0));
}

// A multi-select's limit, or Infinity when it has none.
function multiChoiceMax(field) {
  return typeof field.max === 'number' && field.max > 0 ? field.max : Infinity;
}

function QuickOptionsCard({ config, onSubmit, answerText = null, disabled = false }) {
  const fields = React.useMemo(
    () => (Array.isArray(config && config.fields)
      ? config.fields.filter((field) => field && typeof field === 'object')
      : []),
    [config],
  );

  // A card asking exactly one either/or question needs no Send button: picking
  // a chip IS the answer, so requiring a second click to confirm it is pure
  // friction. Cards with several fields (or a fill-in blank, or a multi-select)
  // still need one -- there is no way to know the user has finished.
  const isSingleChoice = fields.length === 1 && fields[0].type !== 'text'
    && !isMultiChoice(fields[0]);

  // No pre-selection on a single-choice card. `default` earns its place on a
  // multi-field card, where you can accept the defaults and press Send -- but
  // here every answer takes a click, so a highlighted chip is never a choice
  // anyone made. It only reads as one.
  // Worked out on every render, not stored: the config can change under a
  // mounted card (a second card streaming in gets merged into this one), and
  // its new fields need their defaults too.
  const defaults = isSingleChoice ? {} : initialValues(fields);

  // Only what the user picked or typed. State survives re-renders / streaming.
  const [picked, setPicked] = useState({});
  const values = { ...defaults, ...picked };
  const [submitted, setSubmitted] = useState(false);

  // A choice with nothing picked (`default: null`) holds Send back.
  const isMissingChoice = fields.some((field) => isUnansweredChoice(field, values[field.key]));

  // What the user actually replied, parsed back out of their message (the card
  // composes it as "<label>: <value>" per line). Answering re-renders the
  // message list and remounts this component, so its own state is gone by the
  // time the answered card is drawn -- without this it falls back to the
  // pre-selected default and highlights "Plain Word document" no matter what
  // was picked. Reading it from the conversation is also the one source that
  // survives a page reload.
  const answered = React.useMemo(() => {
    if (!answerText) return null;
    const picked = {};
    fields.forEach((field) => {
      const match = String(answerText).match(
        new RegExp(`^${field.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:\\s*(.+)$`, 'm')
      );
      if (!match) return;
      picked[field.key] = isMultiChoice(field)
        ? match[1].split(MULTI_CHOICE_SEPARATOR).map((part) => part.trim()).filter(Boolean)
        : match[1].trim();
    });
    return Object.keys(picked).length ? picked : null;
  }, [answerText, fields]);

  // Selections to paint: the real answer wins over local state, which wins over
  // the configured default.
  const shown = answered || values;

  // Declared after `answered` on purpose -- reading it above would be a
  // temporal-dead-zone ReferenceError, not a subtle bug.
  const isLocked = disabled || submitted || Boolean(answered);

  const setValue = (key, value) => {
    if (isLocked) return;
    setPicked((prev) => ({ ...prev, [key]: value }));
  };

  // Takes the chosen value explicitly rather than reading `values`: setState is
  // asynchronous, so a chip that both selects and sends would otherwise compose
  // its message from the PREVIOUS selection (the default on the first click).
  const submitWith = (overrides) => {
    if (isLocked) return;
    const finalValues = { ...values, ...(overrides || {}) };
    if (fields.some((field) => isUnansweredChoice(field, finalValues[field.key]))) return;
    const composed = fields
      .map((field) => {
        const value = finalValues[field.key];
        const text = Array.isArray(value)
          ? value.join(MULTI_CHOICE_SEPARATOR)
          : String(value ?? '').trim();
        return `${field.label}: ${text}`;
      })
      .join('\n');
    setSubmitted(true);
    if (onSubmit) onSubmit(composed);
  };

  const handleSubmit = () => submitWith();

  const handleChipClick = (field, option) => {
    if (isLocked) return;
    if (isMultiChoice(field)) {
      const current = Array.isArray(values[field.key]) ? values[field.key] : [];
      if (current.includes(option)) {
        setValue(field.key, current.filter((picked) => picked !== option));
      } else if (current.length < multiChoiceMax(field)) {
        // Kept in the card's order, not click order, so the reply lists them
        // the way the card does.
        setValue(field.key, field.options.filter((o) => o === option || current.includes(o)));
      }
      return;
    }
    setValue(field.key, option);
    if (isSingleChoice) submitWith({ [field.key]: option });
  };

  const isChipSelected = (field, option) => (isMultiChoice(field)
    ? Array.isArray(shown[field.key]) && shown[field.key].includes(option)
    : shown[field.key] === option);

  // A multi-select at its limit: only the picked chips stay clickable, to
  // unpick one.
  const isChipBlocked = (field, option) => isMultiChoice(field)
    && !isChipSelected(field, option)
    && Array.isArray(values[field.key])
    && values[field.key].length >= multiChoiceMax(field);

  if (!fields.length) return null;

  return (
    <div className={`kai-options-card${isLocked ? ' kai-options-card--locked' : ''}`}>
      {config.intro ? <div className="kai-options-intro">{config.intro}</div> : null}

      {fields.map((field) => (
        <div className="kai-options-field" key={field.key}>
          {field.intro ? (
            <div className="kai-options-intro kai-options-intro--field">{field.intro}</div>
          ) : null}
          <div className="kai-options-label">
            {field.label}
            {isMultiChoice(field) && Array.isArray(field.options)
              && field.options.length > multiChoiceMax(field) ? (
                <span className="kai-options-limit">Pick up to {multiChoiceMax(field)}</span>
              ) : null}
          </div>

          {field.type === 'text' ? (
            <input
              className="kai-options-input"
              type="text"
              aria-label={field.label}
              value={shown[field.key] ?? ''}
              placeholder={field.placeholder || ''}
              disabled={isLocked}
              onChange={(e) => setValue(field.key, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
            />
          ) : (
            <div className="kai-options-chips">
              {(Array.isArray(field.options) ? field.options : []).map((option) => (
                <button
                  type="button"
                  key={option}
                  className={`kai-options-chip${
                    isChipSelected(field, option) ? ' kai-options-chip--selected' : ''
                  }`}
                  aria-pressed={isMultiChoice(field) ? isChipSelected(field, option) : undefined}
                  disabled={isLocked || isChipBlocked(field, option)}
                  onClick={() => handleChipClick(field, option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}

      {isSingleChoice ? (
        // No button: the chip already sent it. Keep a quiet confirmation so the
        // card still visibly resolves rather than just greying out.
        submitted ? <div className="kai-options-sent">Sent</div> : null
      ) : (
        <button
          type="button"
          className="kai-options-submit"
          disabled={isLocked || isMissingChoice}
          onClick={handleSubmit}
        >
          {submitted ? 'Sent' : config.submit || 'Send'}
          {!isLocked ? <span aria-hidden="true"> →</span> : null}
        </button>
      )}
    </div>
  );
}

export default QuickOptionsCard;
